import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { env } from '@/platform/env';
import api from '@/lib/api';

export const IDEMPOTENCY_HEADER = 'Idempotency-Key';

export class TaskPollingCancelledError extends Error {
  constructor() {
    super('Task polling was cancelled.');
    this.name = 'TaskPollingCancelledError';
  }
}

export const isTaskPollingCancelledError = (error: unknown): error is TaskPollingCancelledError =>
  error instanceof TaskPollingCancelledError;

export const TASK_STATUSES = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  SUCCEEDED: 'succeeded',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled'
} as const;

export type TaskStatus = (typeof TASK_STATUSES)[keyof typeof TASK_STATUSES];

export interface ErpTaskResponse<TResult = unknown> {
  task_id: string;
  task_type: string;
  status: TaskStatus;
  entity_slug: string;
  result_http_status: number | null;
  error_message: string | null;
  retry_count: number;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  updated_at: string;
  result: TResult | null;
}

export interface ErpTaskSubmissionResponse {
  task_id: string;
  status: TaskStatus;
  status_url?: string;
}

export interface GetTaskDetailOptions extends AxiosRequestConfig {
  entitySlug: string;
  taskId: string;
}

export interface PollTaskDetailOptions<TResult = unknown> extends GetTaskDetailOptions {
  statusUrl?: string;
  intervalMs?: number;
  onProgress?: (task: ErpTaskResponse<TResult>) => void;
}

export interface WaitForErpTasksOptions<TResult = unknown> {
  entitySlug: string;
  tasks: ErpTaskSubmissionResponse[];
  intervalMs?: number;
  signal?: AbortSignal;
  onProgress?: (task: ErpTaskResponse<TResult>) => void;
}

interface StoredAsyncTaskOperation {
  signatureHash: string;
  idempotencyKey: string;
  taskId?: string;
}

const ACTIVE_TASK_STATUSES: TaskStatus[] = [TASK_STATUSES.PENDING, TASK_STATUSES.PROCESSING];
const SUCCESS_TASK_STATUSES: TaskStatus[] = [TASK_STATUSES.SUCCEEDED, TASK_STATUSES.COMPLETED];
const TERMINAL_TASK_STATUSES: TaskStatus[] = [...SUCCESS_TASK_STATUSES, TASK_STATUSES.FAILED, TASK_STATUSES.CANCELLED];
const RETRYABLE_SUBMISSION_STATUSES = new Set([401, 500, 502, 503, 504]);
const pendingSubmissionRequests = new Map<string, Promise<AxiosResponse<unknown>>>();
const pendingOperations = new Map<string, StoredAsyncTaskOperation>();

const canonicalize = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(canonicalize);
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (value && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((result, key) => {
        const item = (value as Record<string, unknown>)[key];
        if (item !== undefined) {
          result[key] = canonicalize(item);
        }
        return result;
      }, {});
  }

  return value;
};

const hashSignature = (signature: string) => {
  let hash = 2166136261;

  for (let index = 0; index < signature.length; index += 1) {
    hash ^= signature.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return (hash >>> 0).toString(36);
};

const getOperationStorageKey = (signature: string) => `erp-async-operation:${hashSignature(signature)}`;

const readStoredOperation = (signature: string): StoredAsyncTaskOperation | null => {
  const inMemoryOperation = pendingOperations.get(signature);
  if (inMemoryOperation) return inMemoryOperation;
  if (typeof sessionStorage === 'undefined') return null;

  try {
    const storedValue = sessionStorage.getItem(getOperationStorageKey(signature));
    if (!storedValue) return null;

    const operation = JSON.parse(storedValue) as Partial<StoredAsyncTaskOperation>;
    if (operation.signatureHash !== hashSignature(signature) || typeof operation.idempotencyKey !== 'string') {
      return null;
    }

    pendingOperations.set(signature, operation as StoredAsyncTaskOperation);
    return operation as StoredAsyncTaskOperation;
  } catch {
    return null;
  }
};

const storeOperation = (signature: string, operation: StoredAsyncTaskOperation) => {
  pendingOperations.set(signature, operation);
  if (typeof sessionStorage === 'undefined') return;

  try {
    sessionStorage.setItem(getOperationStorageKey(signature), JSON.stringify(operation));
  } catch {
    // The request can still proceed with the in-memory key when storage is unavailable.
  }
};

const clearStoredOperation = (signature: string) => {
  pendingOperations.delete(signature);
  if (typeof sessionStorage === 'undefined') return;

  try {
    sessionStorage.removeItem(getOperationStorageKey(signature));
  } catch {
    // Ignore unavailable storage; the in-flight registry is still cleared.
  }
};

const clearStoredOperationsForTask = (taskId: string) => {
  pendingOperations.forEach((operation, signature) => {
    if (operation.taskId === taskId) {
      pendingOperations.delete(signature);
    }
  });
  if (typeof sessionStorage === 'undefined') return;

  try {
    const storageKeys = Array.from({ length: sessionStorage.length }, (_, index) => sessionStorage.key(index)).filter(
      (key): key is string => Boolean(key?.startsWith('erp-async-operation:'))
    );

    storageKeys.forEach(storageKey => {
      const storedValue = sessionStorage.getItem(storageKey);
      if (!storedValue) return;

      const operation = JSON.parse(storedValue) as Partial<StoredAsyncTaskOperation>;
      if (operation.taskId === taskId) {
        sessionStorage.removeItem(storageKey);
      }
    });
  } catch {
    // Ignore unavailable or malformed storage entries.
  }
};

const createIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `operation-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const buildSubmissionSignature = (url: string, payload: unknown, config?: AxiosRequestConfig) =>
  JSON.stringify(
    canonicalize({
      url,
      payload,
      params: config?.params ?? null
    })
  );

const withIdempotencyHeader = (config: AxiosRequestConfig | undefined, idempotencyKey: string): AxiosRequestConfig => {
  const sourceHeaders =
    config?.headers && typeof (config.headers as { toJSON?: unknown }).toJSON === 'function'
      ? ((config.headers as { toJSON: () => Record<string, unknown> }).toJSON() as Record<string, unknown>)
      : ({ ...config?.headers } as Record<string, unknown>);
  const headers = Object.entries(sourceHeaders).reduce<Record<string, unknown>>((result, [key, value]) => {
    if (key.toLowerCase() !== 'x-idempotency-key' && key.toLowerCase() !== IDEMPOTENCY_HEADER.toLowerCase()) {
      result[key] = value;
    }
    return result;
  }, {});

  return {
    ...config,
    headers: {
      ...headers,
      [IDEMPOTENCY_HEADER]: idempotencyKey
    }
  };
};

export const postErpAsyncTask = <TResponse = ErpTaskSubmissionResponse, TPayload = unknown>(
  url: string,
  payload: TPayload,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<TResponse>> => {
  const signature = buildSubmissionSignature(url, payload, config);
  const inFlightRequest = pendingSubmissionRequests.get(signature);
  if (inFlightRequest) {
    return inFlightRequest as Promise<AxiosResponse<TResponse>>;
  }

  const operation =
    readStoredOperation(signature) ??
    ({
      signatureHash: hashSignature(signature),
      idempotencyKey: createIdempotencyKey()
    } satisfies StoredAsyncTaskOperation);
  storeOperation(signature, operation);

  const request = api
    .post<TResponse>(url, payload, withIdempotencyHeader(config, operation.idempotencyKey))
    .then(response => {
      if (isErpTaskSubmissionResponse(response.data)) {
        storeOperation(signature, {
          ...operation,
          taskId: response.data.task_id
        });
      } else {
        clearStoredOperation(signature);
      }
      return response;
    })
    .catch(error => {
      const status = error?.response?.status as number | undefined;
      if (status !== undefined && !RETRYABLE_SUBMISSION_STATUSES.has(status)) {
        clearStoredOperation(signature);
      }
      throw error;
    })
    .finally(() => {
      pendingSubmissionRequests.delete(signature);
    });

  pendingSubmissionRequests.set(signature, request as Promise<AxiosResponse<unknown>>);
  return request;
};

export const isTaskActiveStatus = (status: TaskStatus) => ACTIVE_TASK_STATUSES.includes(status);

export const isTaskTerminalStatus = (status: TaskStatus) => TERMINAL_TASK_STATUSES.includes(status);

export const isTaskSuccessStatus = (status: TaskStatus) => SUCCESS_TASK_STATUSES.includes(status);

export const isErpTaskSubmissionResponse = (value: unknown): value is ErpTaskSubmissionResponse => {
  if (!value || typeof value !== 'object') return false;

  const maybeTask = value as Partial<ErpTaskSubmissionResponse>;
  return typeof maybeTask.task_id === 'string' && typeof maybeTask.status === 'string';
};

export const buildTaskDetailEndpoint = (entitySlug: string, taskId: string) =>
  `/entities/${encodeURIComponent(entitySlug)}/erp/tasks/${encodeURIComponent(taskId)}/`;

export const normalizeTaskStatusUrl = (statusUrl: string) => {
  try {
    const apiBaseUrl = new URL(env.apiUrl);
    const resolvedStatusUrl = new URL(statusUrl, `${apiBaseUrl.origin}/`);

    if (resolvedStatusUrl.origin !== apiBaseUrl.origin) {
      return statusUrl;
    }

    const apiBasePath = apiBaseUrl.pathname.replace(/\/+$/, '');
    const normalizedPath =
      resolvedStatusUrl.pathname === apiBasePath
        ? '/'
        : apiBasePath && resolvedStatusUrl.pathname.startsWith(`${apiBasePath}/`)
          ? resolvedStatusUrl.pathname.slice(apiBasePath.length)
          : resolvedStatusUrl.pathname;

    return `${normalizedPath || '/'}${resolvedStatusUrl.search}`;
  } catch {
    return statusUrl;
  }
};

const getObjectValue = (value: unknown, key: string): unknown => {
  if (!value || typeof value !== 'object') return undefined;

  return (value as Record<string, unknown>)[key];
};

export const getErpTaskErrorMessage = (task: ErpTaskResponse): string => {
  const resultErrors = getObjectValue(task.result, 'errors');

  if (Array.isArray(resultErrors)) {
    const errorMessages = resultErrors
      .map(errorItem => getObjectValue(errorItem, 'error'))
      .filter((message): message is string => typeof message === 'string' && message.trim().length > 0);

    if (errorMessages.length > 0) {
      return errorMessages.join('\n');
    }
  }

  return task.error_message || `Task ${task.task_id} ended with status ${task.status}`;
};

export const getErpTaskDetail = async <TResult = unknown>({
  entitySlug,
  taskId,
  ...config
}: GetTaskDetailOptions): Promise<ErpTaskResponse<TResult>> => {
  const response = await api.get<ErpTaskResponse<TResult>>(buildTaskDetailEndpoint(entitySlug, taskId), config);

  return response.data;
};

const waitForNextPoll = (intervalMs: number, signal?: AxiosRequestConfig['signal']) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new TaskPollingCancelledError());
      return;
    }

    const timeoutId = setTimeout(() => {
      signal?.removeEventListener?.('abort', handleAbort);
      resolve();
    }, intervalMs);
    const handleAbort = () => {
      clearTimeout(timeoutId);
      reject(new TaskPollingCancelledError());
    };

    signal?.addEventListener?.('abort', handleAbort, { once: true });
  });

export const pollErpTaskDetail = async <TResult = unknown>({
  statusUrl,
  intervalMs = 1500,
  onProgress,
  ...options
}: PollTaskDetailOptions<TResult>): Promise<ErpTaskResponse<TResult>> => {
  while (true) {
    const latestTask = statusUrl
      ? (await api.get<ErpTaskResponse<TResult>>(normalizeTaskStatusUrl(statusUrl), { signal: options.signal })).data
      : await getErpTaskDetail<TResult>(options);
    onProgress?.(latestTask);

    if (isTaskTerminalStatus(latestTask.status)) {
      clearStoredOperationsForTask(latestTask.task_id);
      return latestTask;
    }

    await waitForNextPoll(intervalMs, options.signal);
  }
};

export const waitForErpTasks = async <TResult = unknown>({
  entitySlug,
  tasks,
  intervalMs = 5000,
  signal,
  onProgress
}: WaitForErpTasksOptions<TResult>): Promise<ErpTaskResponse<TResult>[]> => {
  const taskResults: ErpTaskResponse<TResult>[] = [];

  for (const task of tasks) {
    const taskResult = await pollErpTaskDetail<TResult>({
      entitySlug,
      taskId: task.task_id,
      statusUrl: task.status_url,
      intervalMs,
      signal,
      onProgress
    });

    if (!isTaskSuccessStatus(taskResult.status)) {
      throw new Error(getErpTaskErrorMessage(taskResult));
    }

    taskResults.push(taskResult);
  }

  return taskResults;
};
